import { useState, useCallback, useEffect, useRef } from 'react';
import {
  useAccount,
  useReadContract,
  useWriteContract,
  useWaitForTransactionReceipt,
  useChainId,
  useSwitchChain,
  usePublicClient,
} from 'wagmi';
import { parseUnits, type Hex } from 'viem';
import { polygon } from 'wagmi/chains';
import {
  ERC20_ABI,
  PRESALE_ABI,
  PRESALE_ADDRESS,
  USDT_ADDRESS,
  USDT_DECIMALS,
} from '../lib/contracts';
import { referralApi } from '../api/referral';
import { gasApi } from '../api/gas';

type Step =
  | 'idle'
  | 'switching-chain'
  | 'funding-gas'
  | 'approving'
  | 'approve-confirming'
  | 'buying'
  | 'buy-confirming'
  | 'attributing'
  | 'done'
  | 'error';

interface BuyResult {
  step: Step;
  errorMessage: string | null;
  txHash: Hex | null;
  /** True when the current step has been hanging > 30s. Indicates wallet didn't surface the request. */
  stuck: boolean;
  /** True when wallet is connected but on the wrong chain. UI should show "Switch to Polygon". */
  wrongChain: boolean;
  /** True when running in a mobile browser — UI can show extra guidance. */
  isMobile: boolean;
  /** Manual switch-to-Polygon trigger. */
  switchToPolygon: () => Promise<void>;
  /** Trigger the full flow for a USDT amount (e.g. "150") + optional referral code from URL/localStorage. */
  buy: (usdtAmount: string, referralCode?: string) => Promise<void>;
  /** Reset state; clears any persisted in-flight buy from localStorage. */
  reset: () => void;
}

const PERSIST_KEY = 'altcoins.buyflow';
const STUCK_TIMEOUT_MS = 30_000;
const PERSIST_TTL_MS = 30 * 60 * 1000; // 30 min
const APPROVE_WAIT_MS = 90_000; // max time to wait for the approve to mine before giving up
const GAS_NEEDED_WEI = parseUnits('0.02', 18); // ~min POL to cover approve + buy

interface PersistedState {
  address: string;
  txHash: Hex | null;
  step: Step;
  ts: number;
}

function readPersisted(address: string | undefined): PersistedState | null {
  if (!address || typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(PERSIST_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedState;
    if (parsed.address.toLowerCase() !== address.toLowerCase()) return null;
    if (Date.now() - parsed.ts > PERSIST_TTL_MS) {
      localStorage.removeItem(PERSIST_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writePersisted(state: PersistedState) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(PERSIST_KEY, JSON.stringify(state));
  } catch {
    // ignore quota errors
  }
}

function clearPersisted() {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(PERSIST_KEY);
  } catch {
    // ignore
  }
}

function detectMobile(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

/**
 * Buy flow:
 *   0. Gas perk: if the buyer has ~no POL, ask the backend to drip a little so
 *      they can pay their own gas (non-blocking — never fails the buy).
 *   1. Pre-check: wallet on Polygon? If not, offer switch.
 *   2. Read current USDT allowance for the presale contract.
 *   3. If allowance < amount: send approve(amount), then WAIT for the approve
 *      receipt + confirm the on-chain allowance before continuing.
 *   4. Send buyTokensWithUSDT(amount); store the hash in localStorage.
 *   5. Best-effort POST attribution intent to backend (non-blocking).
 *   6. Mark done.
 */
export function useBuyFlow(): BuyResult {
  const { address } = useAccount();
  const chainId = useChainId();
  const { switchChainAsync } = useSwitchChain();
  const publicClient = usePublicClient({ chainId: polygon.id });

  const [step, setStep] = useState<Step>('idle');
  const [errorMessage, setError] = useState<string | null>(null);
  const [txHash, setTxHash] = useState<Hex | null>(null);
  const [stuck, setStuck] = useState(false);

  const stuckTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMobile = detectMobile();
  const wrongChain = !!address && chainId !== polygon.id;

  // -- Restore persisted state on mount / address change ----------------
  useEffect(() => {
    if (!address) {
      // Disconnected — clear state but don't touch persisted (might be a flicker).
      return;
    }
    const restored = readPersisted(address);
    if (restored && restored.txHash) {
      setTxHash(restored.txHash);
      if (
        restored.step === 'buying' ||
        restored.step === 'buy-confirming' ||
        restored.step === 'attributing'
      ) {
        setStep('buy-confirming');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address]);

  // -- Persist on step changes -----------------------------------------
  useEffect(() => {
    if (!address) return;
    if (step === 'idle' || step === 'done' || step === 'error') {
      clearPersisted();
      return;
    }
    writePersisted({ address, step, txHash, ts: Date.now() });
  }, [step, txHash, address]);

  // -- Stuck-detector watchdog -----------------------------------------
  useEffect(() => {
    if (stuckTimerRef.current) {
      clearTimeout(stuckTimerRef.current);
      stuckTimerRef.current = null;
    }
    setStuck(false);

    if (step === 'approving' || step === 'buying') {
      stuckTimerRef.current = setTimeout(() => setStuck(true), STUCK_TIMEOUT_MS);
    }

    return () => {
      if (stuckTimerRef.current) clearTimeout(stuckTimerRef.current);
    };
  }, [step]);

  // -- Wagmi reads / writes --------------------------------------------
  const { data: allowance, refetch: refetchAllowance } = useReadContract({
    address: USDT_ADDRESS,
    abi: ERC20_ABI,
    functionName: 'allowance',
    args: address ? [address, PRESALE_ADDRESS] : undefined,
    query: { enabled: !!address },
  });

  const { writeContractAsync } = useWriteContract();

  const switchToPolygon = useCallback(async () => {
    setError(null);
    try {
      setStep('switching-chain');
      await switchChainAsync({ chainId: polygon.id });
      setStep('idle');
    } catch (err) {
      const e = err as { shortMessage?: string; message?: string };
      setError(e.shortMessage ?? e.message ?? 'Could not switch network');
      setStep('error');
    }
  }, [switchChainAsync]);

  const reset = useCallback(() => {
    setStep('idle');
    setError(null);
    setTxHash(null);
    setStuck(false);
    clearPersisted();
  }, []);

  const buy = useCallback(
    async (usdtAmount: string, referralCode?: string) => {
      if (!address) {
        setError('Connect your wallet first');
        setStep('error');
        return;
      }
      if (wrongChain) {
        setError('Please switch to Polygon network first');
        setStep('error');
        return;
      }
      try {
        setError(null);
        setTxHash(null);

        // -- Step 0: gas perk — drip POL if the buyer can't afford gas -----
        if (publicClient) {
          try {
            const pol = await publicClient.getBalance({ address });
            if (pol < GAS_NEEDED_WEI) {
              setStep('funding-gas');
              const res = await gasApi.topup(address).catch(() => null);
              if (res?.txHash) {
                const deadline = Date.now() + 60_000;
                while (Date.now() < deadline) {
                  const b = await publicClient.getBalance({ address });
                  if (b >= GAS_NEEDED_WEI) break;
                  await new Promise((r) => setTimeout(r, 3000));
                }
              }
            }
          } catch {
            // never block the buy on the gas perk
          }
        }

        const amount = parseUnits(usdtAmount, USDT_DECIMALS);
        const currentAllowance = (allowance as bigint | undefined) ?? 0n;

        // -- Step 1: approve if needed --------------------------------
        if (currentAllowance < amount) {
          setStep('approving');
          const approveHash = await writeContractAsync({
            address: USDT_ADDRESS,
            abi: ERC20_ABI,
            functionName: 'approve',
            args: [PRESALE_ADDRESS, amount],
          });

          setStep('approve-confirming');

          if (publicClient) {
            try {
              await publicClient.waitForTransactionReceipt({
                hash: approveHash,
                confirmations: 1,
                timeout: APPROVE_WAIT_MS,
              });
            } catch (waitErr) {
              console.warn('approve receipt wait failed, falling back to allowance poll', waitErr);
            }
          }

          const deadline = Date.now() + APPROVE_WAIT_MS;
          let confirmed = false;
          await new Promise((r) => setTimeout(r, 1500));
          while (Date.now() < deadline) {
            const res = await refetchAllowance();
            const fresh = (res.data as bigint | undefined) ?? 0n;
            if (fresh >= amount) {
              confirmed = true;
              break;
            }
            await new Promise((r) => setTimeout(r, 3000));
          }
          if (!confirmed) {
            setError(
              'Your USDT approval is taking longer than expected to confirm. ' +
                'Wait a moment, then tap Buy again — it will continue from here.',
            );
            setStep('error');
            return;
          }
        }

        // -- Step 2: call buyTokensWithUSDT ---------------------------
        setStep('buying');
        const buyHash = await writeContractAsync({
          address: PRESALE_ADDRESS,
          abi: PRESALE_ABI,
          functionName: 'buyTokensWithUSDT',
          args: [amount],
        });
        setTxHash(buyHash);
        setStep('buy-confirming');

        // -- Step 3: best-effort attribution intent --------------------
        if (referralCode) {
          setStep('attributing');
          try {
            await referralApi.attribute({
              txHash: buyHash,
              referralCode,
              walletAddress: address,
            });
          } catch (err) {
            console.warn('Attribution intent submission failed', err);
          }
        }

        setStep('done');
      } catch (err: unknown) {
        const e = err as { shortMessage?: string; message?: string; code?: number };
        if (e.code === 4001 || /reject|denied|cancel/i.test(e.message ?? '')) {
          setError('Transaction cancelled in wallet.');
        } else {
          setError(e.shortMessage ?? e.message ?? 'Transaction failed');
        }
        setStep('error');
      }
    },
    [address, wrongChain, allowance, writeContractAsync, refetchAllowance, publicClient],
  );

  return {
    step,
    errorMessage,
    txHash,
    stuck,
    wrongChain,
    isMobile,
    switchToPolygon,
    buy,
    reset,
  };
}

/** Standalone hook for the component to confirm a tx receipt (separate so it remounts cleanly). */
export function useTxReceipt(hash: Hex | undefined) {
  return useWaitForTransactionReceipt({ hash });
}
